<script>
    import httpService from '@/services/core/http.service';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import utilsService from '@/services/core/utils.service';
    import AdminGrid from '@/components/shared/AdminGrid';
    import Spinner from '@/components/shared/Spinner.vue';
    import modalService from '@/services/core/modal.service';
    import FreshbooksClientInvoicesModal from '@/components/modals/billingHistory/FreshbooksClientInvoicesModal';
    import { DxColumn } from 'devextreme-vue/data-grid';
    import ConfirmModal from '@/components/modals/ConfirmModal';

    export default {
        components: {
            AdminGrid,
            DxColumn,
            ButtonSpinner,
            Spinner
        },
        data() {
            return {
                data: [],
                isLoading: false,
                isUpdating: false,
                showVerificationProfile: 0
            };
        },
        computed: {
            title() {
                if (this.data.length > 0) {
                    const billing = this.data[0];
                    let period = `${billing.month}/${billing.year}`;
                    return `Billing ${period}`;
                }

                return `Billing`;
            }
        },
        methods: {
            checkBillingSettings() {
                const url = `company-billing-history/${this.$route.params.id}/check-invoice-profiles`;
                this.isLoading = true;
                httpService
                    .get(url)
                    .then(() => {
                        utilsService.showToast('Profiles verified successfully');
                    })
                    .finally(() => (this.isLoading = false));
            },
            statusTemplate(cellInfo, item) {
                cellInfo.innerHTML = `<span title='${item.data.freshbooksError || ''}'>${item.data.freshbooksStatus}</span>`;
            },
            load() {
                const url = `company-billing-history/${this.$route.params.id}/billing-detail`;
                this.isLoading = true;
                httpService.get(url).then(data => {
                    this.data = data;
                    this.isLoading = false;
                });
            },
            showLastInvoices(freshbooksClientId) {
                modalService.show(FreshbooksClientInvoicesModal, {
                    freshbooksClientId
                });
            },
            showInvoiceProfile(freshbooksInvoiceProfileId) {
                window.open(`https://my.freshbooks.com/#/recurring-template/${freshbooksInvoiceProfileId}/show`, '_blank');
            },
            goToBilling(companyId) {
                this.$router.push(`/billing/${companyId}`);
            },
            updateFreshbooksProfile(companyId) {
                this.isUpdating = true;
                const url = `company-billing-history/${this.$route.params.id}/invoice-profile/${companyId}`;
                httpService
                    .put(url)
                    .then(() => {
                        utilsService.showToast('Freshbooks invoice profile updated');
                        this.load();
                    })
                    .finally(() => (this.isUpdating = false));
            },
            changeAutoBilling(item) {
                modalService.show(ConfirmModal, {
                    title: item.autoBilling ? 'Disable Auto-billing' : 'Enable Auto-billing',
                    msg: `Are you sure you want to ${item.autoBilling ? 'DISABLE' : 'ENABLE'} ${item.companyName} Auto-billing?`,
                    btnTextOk: item.autoBilling ? 'Disable' : 'Enable',
                    onSuccess: () => {
                        this.isUpdating = true;
                        const url = `companies/${item.companyId}/auto-billing`;
                        httpService
                            .put(url)
                            .then(() => {
                                utilsService.showToast('Company was saved', 'success');
                                this.load();
                            })
                            .finally(() => (this.isUpdating = false));
                    }
                });
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<template>
    <div class="main-content-header">
        <h3>
            <i class="bi bi-receipt" @click="showVerificationProfile++"></i>
            <i class="bi bi-clipboard-check" v-if="showVerificationProfile > 3" @click="checkBillingSettings">Check Billing Settings</i>

            {{ title }}
        </h3>
    </div>
    <div class="main-content-body">
        <div class="d-flex justify-content-center my-2" v-if="isLoading">
            <ButtonSpinner :isLoading="isLoading" />
        </div>
        <Spinner :show="isUpdating" text="Updating..." />
        <AdminGrid
            :allow-column-resizing="true"
            :allow-column-reordering="true"
            :enableStoring="true"
            :data-source="data"
            v-if="!isLoading"
            storageKey="bhDetail"
            :fixColumns="true"
        >
            <DxColumn data-field="companyName" caption="Company" :fixed="true" />
            <DxColumn data-field="hasFreshbooksId" caption="Freshbooks Id" />
            <DxColumn data-field="freshbooksStatus" caption="Status" :cell-template="statusTemplate" />
            <DxColumn data-field="remarks" caption="Remarks" />
            <DxColumn data-field="createdOn" data-type="date" />
            <DxColumn data-field="lastActivity" caption="Last Activity" data-type="date" />
            <DxColumn data-field="totalOil" caption="Oil" data-type="number" />
            <DxColumn data-field="totalGas" caption="Gas" data-type="number" />
            <DxColumn data-field="totalWater" caption="Water" data-type="number" />
            <DxColumn data-field="totalNgl" caption="NGL" data-type="number" />
            <DxColumn data-field="activeProductionPointTypeText" caption="Active Production Point" />
            <DxColumn data-field="allowedEntitiesText" caption="Entity Type" />
            <DxColumn data-field="assetPriceTypeText" caption="Asset Price Type" />
            <DxColumn data-field="activeAssetPrice" caption="Active Asset Price" />
            <DxColumn data-field="inactiveAssetPrice" caption="Inactive Asset Price" />
            <DxColumn data-field="minServiceFee" caption="Min Service Fee" />
            <DxColumn data-field="ignoredProductionPoints" caption="Ignored PP" data-type="number" />
            <DxColumn data-field="discountedAssetCount" caption="Discounted Asset Count" data-type="number" />
            <DxColumn data-field="activeProductionPoints" caption="Current Active PP" data-type="number" />
            <DxColumn data-field="inactiveProductionPoints" caption="Current Inactive PP" data-type="number" />
            <DxColumn data-field="previousActiveProductionPoints" caption="Previous Active PP" data-type="number" />
            <DxColumn data-field="previousInactiveProductionPoints" caption="Previous Inactive PP" data-type="number" />
            <DxColumn data-field="activeProductionPointDiff" caption="Diff Active PP" data-type="number" />
            <DxColumn data-field="inactiveProductionPointDiff" caption="Diff Inactive PP" data-type="number" />
            <DxColumn data-field="freshbooksNextIssueDate" caption="Next Issue Date" data-type="date" />
            <DxColumn data-field="activeBatteryCount" caption="batteries" data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="inactiveBatteryCount" caption="batteries" data-type="number" header-cell-template="inactive-header" />
            <DxColumn data-field="activeCollectionPointCount" caption="CP" data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="inactiveCollectionPointCount" caption="CP" data-type="number" header-cell-template="inactive-header" />
            <DxColumn data-field="activeWellCount" caption="wells" data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="inactiveWellCount" caption="wells" data-type="number" header-cell-template="inactive-header" />
            <DxColumn
                data-field="activeBatteryAsWellCount"
                caption="wells (battery)"
                data-type="number"
                header-cell-template="active-header"
            />
            <DxColumn
                data-field="inactiveBatteryAsWellCount"
                caption="wells (battery)"
                data-type="number"
                header-cell-template="inactive-header"
            />
            <DxColumn data-field="activeUserCount" caption="users" data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="inactiveUserCount" caption="users" data-type="number" header-cell-template="inactive-header" />
            <DxColumn data-field="activePumperUserCount" caption="pumpers" data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="activeExecUserCount" caption="execs" data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="activeGuestUserCount" caption="guest" data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="activeTransferCount" caption="transfers" data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="inactiveTransferCount" caption="transfers" data-type="number" header-cell-template="inactive-header" />
            <DxColumn data-field="activeInjectionWellCount" caption="I.W." data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="inactiveInjectionWellCount" caption="I.W." data-type="number" header-cell-template="inactive-header" />
            <DxColumn data-field="activeSWDCount" caption="SWD" data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="inactiveSWDCount" caption="SWD" data-type="number" header-cell-template="inactive-header" />
            <DxColumn data-field="activeSourceWellCount" caption="source wells" data-type="number" header-cell-template="active-header" />
            <DxColumn
                data-field="inactiveSourceWellCount"
                caption="source wells"
                data-type="number"
                header-cell-template="inactive-header"
            />
            <DxColumn data-field="activeProducerCount" caption="producer" data-type="number" header-cell-template="active-header" />
            <DxColumn data-field="inactiveProducerCount" caption="producer" data-type="number" header-cell-template="inactive-header" />
            <template #actions="{ item }">
                <button
                    v-if="item.data.freshbooksInvoiceProfileId > 0"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    title="View Invoice Profile"
                    @click="showInvoiceProfile(item.data.freshbooksInvoiceProfileId)"
                >
                    <i class="bi bi-file-earmark-ruled" style="color: #4287f5"></i>
                </button>

                <button
                    v-if="item.data.freshbooksId > 0"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    title="View Last Invoices"
                    @click="showLastInvoices(item.data.freshbooksId)"
                >
                    <i class="bi bi-card-list" style="color: green"></i>
                </button>

                <button type="button" class="btn btn-link py-0 lh-1" title="Billing settings" @click="goToBilling(item.data.companyId)">
                    <i class="bi bi-gear" style="color: grey"></i>
                </button>

                <button
                    v-if="item.data.freshbooksId > 0"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    title="Update freshbooks invoice profile"
                    @click="updateFreshbooksProfile(item.data.companyId)"
                >
                    Update
                </button>

                <button
                    v-if="!item.data.status"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    style="font-size: 1.2rem"
                    title="Auto-billing"
                    @click="changeAutoBilling(item.data)"
                >
                    <i :class="item.data.autoBilling ? 'bi bi-toggle-on' : 'bi bi-toggle-off'"></i>
                </button>
            </template>
        </AdminGrid>
    </div>
</template>
